import type { UiState } from 'instantsearch.js';
import { routeStateToIndexUiState } from '@repo/utils/algoliaCleanUrlStateMapping';
import { ALGOLIA_DATA_HIERARCHICAL_PATH_SEPARATOR } from '@repo/utils/algoliaConstants';

function getHierarchicalMenuFromPath(
  pathname: string,
  hierarchyAttribute: string
) {
  const cleanPath = pathname?.replace(/^\/shop\//, '').replace(/\/$/, '');
  const segments = cleanPath
    ? cleanPath.split('/').map(segment => decodeURIComponent(segment))
    : [];
  const hierarchicalPath = segments.join(
    ALGOLIA_DATA_HIERARCHICAL_PATH_SEPARATOR
  );

  if (!hierarchicalPath) {
    return {};
  }

  return {
    [hierarchyAttribute]: [hierarchicalPath],
  };
}

function getRouteState(
  urlParams: Record<string, unknown>,
  indexName: string
): Record<string, unknown> {
  const nestedIndexState = urlParams[indexName];

  if (
    nestedIndexState &&
    typeof nestedIndexState === 'object' &&
    !Array.isArray(nestedIndexState)
  ) {
    return nestedIndexState as Record<string, unknown>;
  }

  return urlParams;
}

export const generateInitialStateFromUrl = (
  pathname: string,
  indexName: string,
  hierarchyAttribute: string,
  search?: string,
  options?: Parameters<typeof routeStateToIndexUiState>[1]
): UiState => {
  const hierarchicalMenu = getHierarchicalMenuFromPath(
    pathname,
    hierarchyAttribute
  );
  let searchQueryString = '';
  if (search) {
    searchQueryString = search.startsWith('?') ? search.slice(1) : search;
  }
  let urlParams: Record<string, unknown> = {};
  if (searchQueryString) {
    urlParams = Object.fromEntries(new URLSearchParams(searchQueryString));
  }
  const indexParams = routeStateToIndexUiState(
    getRouteState(urlParams, indexName),
    options
  );

  const urlHierarchicalMenu =
    (indexParams?.hierarchicalMenu as Record<string, string[]> | undefined) ||
    {};

  const mergedState = {
    ...indexParams,
    hierarchicalMenu: {
      ...hierarchicalMenu,
      ...urlHierarchicalMenu,
    },
  };

  return {
    [indexName]: mergedState,
  } as UiState;
};
